import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  input,
  output,
  inject,
} from '@angular/core';

import { FnBindPipe } from '@core/common-legacy';
import type { Placement } from '@core/placements-types';
import type { Stage } from '@core/stages-types';
import { StageCategory, StageGroup } from '@core/stages-types';
import {
  ButtonComponent,
  ButtonDropdownIndicatorDirective,
  ConnectedOverlayDirective,
  IconComponent,
  OverlayOriginDirective,
  TruncateComponent,
} from '@core/user-interface-legacy';
import { WalkthroughStepOriginDirective } from '@core/walkthrough-ui';

import type {
  PlacementActions,
  ProceedEvent,
  ShowAutomatedActionDetailsEvent,
} from '../../candidate-profile-placements/types';
import { CandidateStageMenuComponent } from '../../candidate-stage-menu/candidate-stage.menu.component';
import { StagesWithProgressService } from '../../candidate-stage-menu/stages-with-progress.service';

@Component({
  selector: 'rt-stage-select',
  templateUrl: './stage-select.component.html',
  styleUrls: ['./stage-select.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonComponent,
    IconComponent,
    TruncateComponent,
    CandidateStageMenuComponent,
    ConnectedOverlayDirective,
    OverlayOriginDirective,
    ButtonDropdownIndicatorDirective,
    WalkthroughStepOriginDirective,
    FnBindPipe,
  ],
})
export class StageSelectComponent {
  private readonly stagesWithProgressService = inject(StagesWithProgressService);
  private readonly cdRef = inject(ChangeDetectorRef);

  public placement = input.required<Placement>();

  public stages = input.required<Stage[]>();

  public placementActions = input.required<PlacementActions>();
  public stageAutomationsFetchHandler = input<(pipelineTemplateId: number) => void>();
  public stage = computed(() => this.placement().stage);

  public stagesWithProgress = computed(() => {
    const visibleStages = this.stages().filter(
      stage =>
        !stage.locked ||
        (stage.locked &&
          (stage.category === StageCategory.Apply || stage.category === StageCategory.Sourced)),
    );
    return this.stagesWithProgressService.get(
      visibleStages,
      this.placement(),
      this.placementActions().proceed,
      this.placementActions().hire,
    );
  });

  public StageGroup = StageGroup;

  public proceedClick = output<ProceedEvent>();

  public showAutomatedActionDetailsClick = output<ShowAutomatedActionDetailsEvent>();

  public toggleDropdown(stageDropdown: ConnectedOverlayDirective) {
    if (this.placement().offer?.pipelineTemplateId) {
      this.stageAutomationsFetchHandler()?.(this.placement().offer?.pipelineTemplateId!);
    }
    stageDropdown.open = !stageDropdown.open;
    this.cdRef.detectChanges();
  }

  public beforeWalkthroughTooltipRender(dialog: ConnectedOverlayDirective) {
    this.toggleDropdown(dialog);
  }

  public afterWalkthroughTooltipDispose(dialog: ConnectedOverlayDirective) {
    this.toggleDropdown(dialog);
  }
}
